'use client';

import { Check, Copy, Download, EllipsisVertical, Menu, MonitorDown, PlusSquare, Share, Smartphone, SquareArrowUp } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { AppIcon } from '@/components/pwa/app-icon';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/overlays';
import { detectDevice, startInstall, usePwa, type DeviceInfo, type Platform } from '@/lib/pwa';

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 rounded-[12px] border border-border-soft bg-bg-2/60 p-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-lime text-[12px] font-bold text-on-lime">{i + 1}</span>
          <span className="pt-0.5 text-[13.5px] leading-snug text-fg">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/** Inline icon chip, so the step text shows exactly what to look for on screen. */
function Ico({ children }: { children: ReactNode }) {
  return <span className="mx-0.5 inline-flex size-6 items-center justify-center rounded-md border border-border bg-card align-middle text-fg [&_svg]:size-3.5">{children}</span>;
}

function CopyLink({ label }: { label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const url = `${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.message('Copie este endereço', { description: url });
    }
  };
  return (
    <Button variant="secondary" size="sm" className="w-full" onClick={copy}>
      {copied ? <Check /> : <Copy />}
      {copied ? 'Link copiado' : label}
    </Button>
  );
}

const AFTER = 'Abra o Invexa pelo novo ícone na tela inicial e entre com seu e-mail e senha.';

/** iPhone: Safari and Chrome both install the app (iOS 16.4+); opens on the browser in use. */
function IosGuide({ device }: { device: DeviceInfo }) {
  const detected = device.platform === 'ios' && device.browser !== 'safari' && device.browser !== 'other' ? 'chrome' : 'safari';
  const [browser, setBrowser] = useState<'safari' | 'chrome'>(detected);
  const safari = browser === 'safari';
  // Before iOS 16.4 only Safari could add web apps to the home screen.
  const oldIos = device.iosVersion !== null && device.iosVersion < 16;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1 rounded-[10px] border border-border-soft bg-bg-2/60 p-1">
        {(['safari', 'chrome'] as const).map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBrowser(b)}
            className={`h-8 rounded-[8px] text-[13px] font-medium transition-colors ${browser === b ? 'bg-card text-fg shadow-sm' : 'text-fg-3 hover:text-fg'}`}
          >
            {b === 'safari' ? 'Safari' : 'Google Chrome'}
          </button>
        ))}
      </div>
      {safari ? (
        <Steps
          items={[
            <>Toque em <b>Compartilhar</b> <Ico><Share /></Ico> na barra do Safari. No iOS 26, ele fica no menu <Ico><EllipsisVertical className="rotate-90" /></Ico> ao lado do endereço.</>,
            <>Role a lista e toque em <b>Adicionar à Tela de Início</b> <Ico><PlusSquare /></Ico>.</>,
            <>Deixe <b>Abrir como App Web</b> ligado e toque em <b>Adicionar</b>.</>,
            AFTER,
          ]}
        />
      ) : (
        <>
          <Steps
            items={[
              <>No Chrome, toque em <b>Compartilhar</b> <Ico><SquareArrowUp /></Ico> no lado direito da barra de endereço (ou no menu <Ico><EllipsisVertical className="rotate-90" /></Ico> › <b>Compartilhar</b>).</>,
              <>Role a lista e toque em <b>Adicionar à Tela de Início</b> <Ico><PlusSquare /></Ico>.</>,
              <>Confirme em <b>Adicionar</b>.</>,
              AFTER,
            ]}
          />
          {oldIos ? (
            <>
              <p className="text-xs text-warn">Seu iOS é anterior ao 16.4: no Chrome não aparece a opção. Atualize o iPhone ou abra o Invexa no Safari.</p>
              <CopyLink label="Copiar link para abrir no Safari" />
            </>
          ) : (
            <p className="text-xs text-fg-3">Não apareceu a opção? Atualize o Chrome na App Store, ou use o Safari (aba ao lado).</p>
          )}
        </>
      )}
      <p className="rounded-[10px] bg-lime-soft px-3 py-2 text-xs leading-relaxed text-lime">Instalando pelo Safari ou pelo Chrome, o app é o mesmo. No iPhone, os avisos do robô (notificações) só funcionam no app instalado, com iOS 16.4 ou mais novo.</p>
    </div>
  );
}

function AndroidGuide({ device, canPrompt }: { device: DeviceInfo; canPrompt: boolean }) {
  const steps =
    device.browser === 'samsung'
      ? [
          <>Toque no menu <Ico><Menu /></Ico> no canto inferior direito.</>,
          <>Toque em <b>Adicionar página a</b> e escolha <b>Tela inicial</b> (ou toque no ícone <Ico><Download /></Ico> na barra de endereço).</>,
          <>Confirme em <b>Adicionar</b>.</>,
          AFTER,
        ]
      : device.browser === 'firefox'
        ? [
            <>Toque no menu <Ico><EllipsisVertical /></Ico>.</>,
            <>Toque em <b>Instalar</b> (ou <b>Adicionar à tela inicial</b>).</>,
            <>Confirme em <b>Adicionar</b>.</>,
            AFTER,
          ]
        : device.browser === 'edge'
          ? [
              <>Toque no menu <Ico><EllipsisVertical className="rotate-90" /></Ico> na barra inferior.</>,
              <>Toque em <b>Adicionar ao telefone</b>.</>,
              <>Confirme em <b>Instalar</b>.</>,
              AFTER,
            ]
          : [
              <>Toque no menu <Ico><EllipsisVertical /></Ico> no canto superior direito do Chrome.</>,
              <>Toque em <b>Instalar app</b> (ou <b>Adicionar à tela inicial</b>).</>,
              <>Confirme em <b>Instalar</b>.</>,
              AFTER,
            ];
  return (
    <div className="space-y-3">
      {canPrompt && (
        <Button variant="primary" size="lg" className="w-full" onClick={() => void startInstall()}>
          <Download /> Instalar agora
        </Button>
      )}
      {canPrompt && <p className="text-center text-xs text-fg-3">Ou instale pelo menu do navegador:</p>}
      <Steps items={steps} />
    </div>
  );
}

function DesktopGuide({ device, canPrompt }: { device: DeviceInfo; canPrompt: boolean }) {
  const steps =
    device.browser === 'safari'
      ? [<>No menu <b>Arquivo</b>, clique em <b>Adicionar ao Dock</b>.</>, <>Clique em <b>Adicionar</b>.</>]
      : device.browser === 'firefox'
        ? [<>O Firefox para computador não instala apps. Abra o Invexa no <b>Chrome</b> ou no <b>Edge</b> e clique no ícone de instalar na barra de endereço.</>]
        : [
            <>Clique no ícone <Ico><MonitorDown /></Ico> no fim da barra de endereço.</>,
            <>Ou abra o menu <Ico><EllipsisVertical /></Ico> › <b>Transmitir, salvar e compartilhar</b> › <b>Instalar página como app</b>.</>,
            <>Clique em <b>Instalar</b>. O Invexa abre em uma janela própria.</>,
          ];
  return (
    <div className="space-y-3">
      {canPrompt && (
        <Button variant="primary" size="lg" className="w-full" onClick={() => void startInstall()}>
          <Download /> Instalar agora
        </Button>
      )}
      <Steps items={steps} />
    </div>
  );
}

const TAB_LABEL: Record<Platform, string> = { ios: 'iPhone', android: 'Android', desktop: 'Computador' };

/** Step-by-step install guide; opens on the visitor's own platform, with tabs in case it guessed wrong. */
export function InstallGuide() {
  const open = usePwa((s) => s.guideOpen);
  const close = usePwa((s) => s.closeGuide);
  const canPrompt = usePwa((s) => s.installEvent !== null);
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [tab, setTab] = useState<Platform>('ios');
  useEffect(() => {
    if (!open) return;
    const d = detectDevice();
    setDevice(d);
    setTab(d.platform);
  }, [open]);
  if (!device) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent title="Instalar o app Invexa" description="Acesso em um toque, tela cheia e avisos do robô no celular.">
        <div className="mb-4 flex items-center gap-3 rounded-[12px] border border-border-soft bg-bg-2/60 p-3">
          <AppIcon className="size-12" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Invexa</p>
            <p className="text-xs text-fg-3">Atualiza sozinho: você instala uma vez e pronto.</p>
          </div>
        </div>
        {device.inApp ? (
          <div className="space-y-3">
            <p className="rounded-[12px] border border-warn/30 bg-warn-soft p-3 text-[13px] leading-snug text-warn">
              Você está no navegador do <b>{device.inApp}</b>, que não instala apps. Toque em <b>•••</b> e escolha <b>Abrir no navegador</b> ({device.platform === 'ios' ? 'Safari' : 'Chrome'}), ou copie o link abaixo.
            </p>
            <CopyLink label="Copiar link do Invexa" />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as Platform)}>
            <TabsList className="mb-3 grid w-full grid-cols-3">
              {(['ios', 'android', 'desktop'] as const).map((p) => (
                <TabsTrigger key={p} value={p} className="gap-1.5">
                  {p === 'desktop' ? <MonitorDown className="size-3.5 shrink-0 max-[400px]:hidden" /> : <Smartphone className="size-3.5 shrink-0 max-[400px]:hidden" />}
                  {TAB_LABEL[p]}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="ios">
              <IosGuide device={device.platform === 'ios' ? device : { ...device, browser: 'safari' }} />
            </TabsContent>
            <TabsContent value="android">
              <AndroidGuide device={device.platform === 'android' ? device : { ...device, browser: 'chrome' }} canPrompt={canPrompt && device.platform === 'android'} />
            </TabsContent>
            <TabsContent value="desktop">
              <DesktopGuide device={device.platform === 'desktop' ? device : { ...device, browser: 'chrome' }} canPrompt={canPrompt && device.platform === 'desktop'} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
